/**
 * Notice Action Handlers
 * Handles actions embedded in user notices (accept/decline/block)
 */

import { socialApi } from '../endpoints/social';
import { sessionsApi } from '../endpoints/sessions';
import { adventureTransfersApi } from '../endpoints/adventureTransfers';
import { tripsApi } from '../endpoints/trips';
import { groupPlayApi } from '../endpoints/groupPlay';
import { NoticeTypeIds, type FriendRequestData, type GroupInvitationData, type SessionInviteNoticeData } from '../types/notices';

/**
 * Execute a notice action based on the action type
 */
export async function executeNoticeAction(
  noticeType: string,
  actionType: string,
  actionData: string,
  parsedData?: any
): Promise<void> {
  console.log('Executing notice action:', { noticeType, actionType, actionData, parsedData });

  switch (noticeType.toUpperCase()) {
    case NoticeTypeIds.FRIEND_REQUEST.toUpperCase():
      await handleFriendRequestAction(actionType, actionData, parsedData);
      break;

    case NoticeTypeIds.GROUP_INVITATION.toUpperCase():
      await handleGroupInvitationAction(actionType, actionData, parsedData);
      break;

    case NoticeTypeIds.ADVENTURE_INVITATION.toUpperCase():
      await handleAdventureInvitationAction(actionType, actionData, parsedData);
      break;

    case NoticeTypeIds.SESSION_INVITE.toUpperCase():
      await handleSessionInviteAction(actionType, actionData, parsedData);
      break;

    case NoticeTypeIds.ADVENTURE_TRANSFER_OFFERED.toUpperCase():
      await handleAdventureTransferAction(actionType, actionData);
      break;

    case NoticeTypeIds.TRIP_INVITATION.toUpperCase():
      await handleTripInviteAction(actionType, actionData);
      break;

    case NoticeTypeIds.SESSION_SPLIT_PROPOSED.toUpperCase():
      await handleSessionSplitAction(actionType, actionData);
      break;

    case NoticeTypeIds.STORY_PARTY_INVITE.toUpperCase():
      await handleStoryPartyAction(actionType, actionData);
      break;

    case NoticeTypeIds.STORY_PARTY_UPDATE.toUpperCase():
    case NoticeTypeIds.STORY_PARTY_STOP_STARTED.toUpperCase():
    case NoticeTypeIds.STORY_PARTY_DRIVE_STARTED.toUpperCase():
    case NoticeTypeIds.MODERATOR_APPROVAL_REQUEST.toUpperCase():
    case NoticeTypeIds.SESSION_MATCH_AVAILABLE.toUpperCase():
    case NoticeTypeIds.SESSION_SCHEDULE_DECIDED.toUpperCase():
    case NoticeTypeIds.GROUP_SESSION_UPDATE.toUpperCase():
      // Navigation-only — these carry no buttons, only a place to go.
      return;

    default:
      console.warn('Unknown notice type for action:', noticeType);
      throw new Error(`Unsupported notice type: ${noticeType}`);
  }
}

/**
 * Answer an alternative outing drafted for people the agreed time did not suit.
 *
 * `actionData` is the proposal id. Nobody is enrolled by the notice arriving — accepting is the
 * only thing that creates the session, and declining or ignoring it leaves none behind.
 */
async function handleSessionSplitAction(actionType: string, actionData: string): Promise<void> {
  switch (actionType) {
    case 'accept_session_split':
      await sessionsApi.acceptSplit(actionData);
      break;

    case 'decline_session_split':
      await sessionsApi.declineSplit(actionData);
      break;

    default:
      throw new Error(`Unknown split proposal action: ${actionType}`);
  }
}

/**
 * Handle an offer of an adventure.
 *
 * `actionData` is the transfer id — not the adventure's. Nothing moves until this is answered,
 * and the offer lapses after 30 days, so a stale one is expected rather than exceptional.
 */
async function handleAdventureTransferAction(actionType: string, actionData: string): Promise<void> {
  switch (actionType) {
    case 'accept_adventure_transfer':
      await adventureTransfersApi.accept(actionData);
      break;

    case 'decline_adventure_transfer':
      await adventureTransfersApi.decline(actionData);
      break;

    default:
      throw new Error(`Unsupported adventure transfer action: ${actionType}`);
  }
}

/**
 * Handle friend request actions
 */
async function handleFriendRequestAction(
  actionType: string,
  actionData: string,
  parsedData?: FriendRequestData
): Promise<void> {
  const connectionId = parsedData?.connectionRequestId || actionData;

  switch (actionType) {
    case 'accept_friend_request':
      await socialApi.acceptFriendRequest(connectionId);
      break;

    case 'decline_friend_request':
      await socialApi.rejectFriendRequest(connectionId);
      break;

    case 'block_user':
      const userId = parsedData?.requesterId;
      if (!userId) {
        throw new Error('User ID not found in friend request data');
      }
      await socialApi.blockUser({ userId });
      break;

    default:
      throw new Error(`Unknown friend request action: ${actionType}`);
  }
}

/**
 * Handle a trip invitation.
 *
 * `actionData` is the *share* id rather than the trip's — the share is the thing being answered,
 * and one trip has many of them.
 */
async function handleTripInviteAction(actionType: string, actionData: string): Promise<void> {
  switch (actionType) {
    case 'accept_trip_share':
      await tripsApi.acceptInvite(actionData);
      break;

    case 'decline_trip_share':
      await tripsApi.declineInvite(actionData);
      break;

    default:
      throw new Error(`Unsupported trip invitation action: ${actionType}`);
  }
}

/**
 * Handle group invitation actions
 */
async function handleGroupInvitationAction(
  actionType: string,
  actionData: string,
  parsedData?: GroupInvitationData
): Promise<void> {
  const groupId = parsedData?.groupId || actionData;

  switch (actionType) {
    case 'accept_group_invite':
      await socialApi.acceptGroupInvite(groupId);
      break;

    case 'decline_group_invite':
      await socialApi.declineGroupInvite(groupId);
      break;

    case 'block_group':
      await socialApi.blockGroup(groupId);
      break;

    default:
      throw new Error(`Unknown group invitation action: ${actionType}`);
  }
}

/**
 * Handle adventure invitation actions
 */
async function handleAdventureInvitationAction(
  actionType: string,
  actionData: string,
  parsedData?: any
): Promise<void> {
  // TODO: Implement when adventure invitation endpoints are available
  console.warn('Adventure invitation actions not yet implemented');
  throw new Error('Adventure invitation functionality not yet available');
}

/**
 * Handle session invite actions
 * The actionData from the notice is the sessionId.
 * Accept uses joinSession since the backend resolves the invite from the authenticated user context.
 */
async function handleSessionInviteAction(
  actionType: string,
  actionData: string,
  parsedData?: SessionInviteNoticeData
): Promise<void> {
  const sessionId = parsedData?.SessionId || actionData;

  if (!sessionId) {
    throw new Error('Session ID not found in session invite data');
  }

  switch (actionType) {
    case 'accept_session_invite':
      await sessionsApi.joinSession(sessionId);
      break;

    case 'decline_session_invite':
      await sessionsApi.declineMyInvite(sessionId);
      break;

    default:
      throw new Error(`Unknown session invite action: ${actionType}`);
  }
}

/**
 * Doing a Story together: `actionData` is the party id. Accepting starts the Story for this person once the server has
 * checked they can play where the group is going; a refusal comes back as a 409 with words to show.
 */
async function handleStoryPartyAction(actionType: string, actionData: string): Promise<void> {
  switch (actionType) {
    case 'accept_story_party':
      await groupPlayApi.acceptStoryParty(actionData);
      break;
    case 'decline_story_party':
      await groupPlayApi.declineStoryParty(actionData);
      break;
    default:
      throw new Error(`Unknown Story party action: ${actionType}`);
  }
}

/**
 * Get user-friendly action button variant
 */
export function getActionButtonVariant(actionType: string): 'contained' | 'outlined' | 'text' {
  if (actionType.includes('accept') || actionType.includes('join')) {
    return 'contained';
  }
  if (actionType.includes('block')) {
    return 'outlined';
  }
  return 'outlined';
}

/**
 * Get user-friendly action button color
 */
export function getActionButtonColor(
  actionType: string
): 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' {
  if (actionType.includes('accept') || actionType.includes('join')) {
    return 'primary';
  }
  if (actionType.includes('block') || actionType.includes('decline') || actionType.includes('deny')) {
    return 'error';
  }
  return 'secondary';
}
